import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useStore } from '../../../store';
import { ISummaryData } from '../../../types';
import { validateEmail } from './helper';
import { useChangeEmailMutation } from './useChangeEmailMutation';
import { useLogoutQuery } from '../../../hooks/query/useLogoutQuery';
import { VIEWS_MAP } from '../../../router/constants';

export interface ChangeEmailProps {
  onClose: () => void;
}

interface UseChangeEmailReturn {
  newEmail: string;
  errorMessage: string | undefined;
  isLoading: boolean;
  onSubmit: () => void;
  onEmailInputChanged: (input: string) => void;
  email: string;
  isSuccess: boolean;
  onClose: () => void;
  onBlur: () => void;
  isValid: boolean;
  onFinish: () => void;
  isLoadingLogOut: boolean;
}

export const useChangeEmailHook = ({ onClose }: ChangeEmailProps): UseChangeEmailReturn => {
  const { t } = useTranslation();
  const {
    customer: { email, externalId },
  }: ISummaryData = useStore((state) => state.summary);
  const navigate = useNavigate();

  const { customerId } = useStore((state) => state.currUser());
  const [newEmail, setNewEmail] = useState('');
  const [isSuccess, setIsSuccess] = useState(false);
  const [isValid, setIsValid] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);
  const { refetch: sendLogout, isLoading: isLoadingLogOut } = useLogoutQuery({
    customerId: externalId,
    onSuccess: () => {
      localStorage.clear();
      navigate(`/${VIEWS_MAP.LOGIN}`);
    },
  });

  const onSuccessfulChange = useCallback(() => {
    setIsSuccess(true);
  }, [newEmail]);

  const onFinish = useCallback(() => {
    sendLogout();
  }, []);

  const onError = useCallback((e: any) => {
    if (e.code === 4024) {
      setErrorMessage(t('CHANGE_EMAIL.ERRORS.FORMAT'));
    } else if (e.code === 4041) {
      setErrorMessage(t('CHANGE_EMAIL.ERRORS.ALREADY_IN_USE'));
    } else {
      setErrorMessage(e.message);
    }
  }, []);

  const { isLoading, mutate: mutateChangeEmail } = useChangeEmailMutation({ customerId }, onSuccessfulChange, onError);
  const onSubmit = useCallback(() => {
    setErrorMessage(undefined);
    mutateChangeEmail({ newEmail });
  }, [newEmail, mutateChangeEmail]);

  const onEmailInputChanged = useCallback(
    (input: string) => {
      setNewEmail(input);
      setErrorMessage(undefined);
      if (!validateEmail(input)) {
        setIsValid(false);
      } else if (input === email) {
        setErrorMessage(t('CHANGE_EMAIL.ERRORS.SAME_EMAIL'));
        setIsValid(false);
      } else {
        setErrorMessage(undefined);
        setIsValid(true);
      }
    },
    [t, email],
  );

  const onBlur = useCallback(() => {
    if (newEmail.length < 1) {
      setErrorMessage(t('CHANGE_EMAIL.ERRORS.THIS_FIELD_IS_REQUIRED'));
      setIsValid(false);
    } else if (!validateEmail(newEmail)) {
      setErrorMessage(t('CHANGE_EMAIL.ERRORS.FILL_VALID_EMAIL'));
      setIsValid(false);
    } else if (newEmail === email) {
      setErrorMessage(t('CHANGE_EMAIL.ERRORS.SAME_EMAIL'));
      setIsValid(false);
    } else {
      setErrorMessage(undefined);
    }
  }, [newEmail]);

  return {
    newEmail,
    errorMessage,
    isLoading,
    onSubmit,
    onEmailInputChanged,
    email,
    onClose,
    isSuccess,
    onBlur,
    isValid,
    onFinish,
    isLoadingLogOut,
  };
};
